export const M3_UNIT_16 = {
  id: "m3-u16-likes-social-plans",
  title: "Unit 16: Likes, Dislikes & Social Plans",
  topic: "Express your opinions about everything! Master how to make invitations, accept or decline plans, and talk about what you love or hate. Perfect for building friendships and social life in English!",
  iconType: "heart",
  grammarImage: "/m3-u16-likes-social.jpg",
  grammarText: "Learn to express opinions with like/love/hate, make social invitations, talk about school and work preferences, and integrate all your B1 skills! Final unit of Module 3 - you're ready for real conversations!",
  sets: [
    // ===================================================================
    // SET 1: LIKE/LOVE/HATE (Opinions - Present Simple) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set1",
      title: "Like, Love & Hate",
      questions: [
        {
          type: "mc",
          q: "I _____ soccer. It's my favorite sport!",
          options: ["like", "like me", "am like"],
          a: "like",
          tip: "'I like' + noun! NOT 'me gusta' structure! No 'me'!",
          successtip: "✓ I LIKE! Not 'me'! ⚽ Spanish = me gusta! English = I like! Different structure! No translation!"
        },
        {
          type: "mc",
          q: "She _____ cooking. She does it every day!",
          options: ["loves", "love", "loving"],
          a: "loves",
          tip: "'Loves' = third person! Strong positive feeling!",
          successtip: "✓ LOVES! Third person! 👩‍🍳 He/She/It = loves (with S)! Strong positive emotion!"
        },
        {
          type: "mc",
          q: "They _____ working on weekends. No free time!",
          options: ["hate", "hates", "hating"],
          a: "hate",
          tip: "'Hate' = strong negative! They = no S!",
          successtip: "✓ HATE! Strong negative! 😫 Weekends = family time! Work = no fun! Valid feeling!"
        },
        {
          type: "mc",
          q: "I don't _____ studying at night. I'm too tired!",
          options: ["like", "likes", "am like"],
          a: "like",
          tip: "Don't + base form! I don't LIKE (no 's')!",
          successtip: "✓ DON'T LIKE! Negative opinion! 😴 Night = tired! Valid preference! Self-care matters!"
        },
        {
          type: "mc",
          q: "ERROR TÍPICO: Which is WRONG? '_____ soccer.'",
          options: ["I like me", "I like", "I love"],
          a: "I like me",
          tip: "NEVER say 'I like me X'! That's Spanish structure!",
          successtip: "✓ I LIKE ME = WRONG! 🚫 Common error! Spanish = me gusta! English = I like! Drop 'me'!"
        },
        {
          type: "mc",
          q: "We _____ going to the movies. It's fun!",
          options: ["love", "loves", "loving"],
          a: "love",
          tip: "'We love' + gerund (-ing)! Plural = no S!",
          successtip: "✓ LOVE going! Activity opinion! 🎬 We = no S! Love + -ing = hobby! Cinema = entertainment!"
        },
        {
          type: "mc",
          q: "He doesn't _____ his job. He wants to quit!",
          options: ["like", "likes", "liked"],
          a: "like",
          tip: "Doesn't + base form! He doesn't LIKE (no 's')!",
          successtip: "✓ DOESN'T LIKE! Negative + base! 💼 Unhappy at work = want change! Career dissatisfaction!"
        },
        {
          type: "mc",
          q: "My kids _____ playing outside. Fresh air is good!",
          options: ["love", "loves", "loving"],
          a: "love",
          tip: "'Kids' = they = love! Plural = no S!",
          successtip: "✓ LOVE playing! Kids + outdoors! 🏃 Children = energy! Outside play = healthy! Parenting win!"
        },
        {
          type: "mc",
          q: "I _____ working overtime. I need the money.",
          options: ["don't like", "doesn't like", "not like"],
          a: "don't like",
          tip: "'I don't like' = honest negative! Present simple negative!",
          successtip: "✓ DON'T LIKE! Honest struggle! 💰 Need money BUT hate overtime! Immigrant reality! Hustle!"
        },
        {
          type: "mc",
          q: "She _____ her coworkers. They're very friendly!",
          options: ["loves", "love", "loving"],
          a: "loves",
          tip: "'She loves' = third person! Positive workplace!",
          successtip: "✓ LOVES! Workplace happiness! 👥 Good coworkers = better life! Team matters! Lucky her!"
        }
      ]
    },

    // ===================================================================
    // SET 2: School Subjects (vocabulary + zero article) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set2",
      title: "School Subjects",
      questions: [
        {
          type: "mc",
          q: "I like _____ because it's interesting.",
          options: ["math", "the math", "a math"],
          a: "math",
          tip: "NO article with subjects! Math, English, history = bare noun!",
          successtip: "✓ MATH! Zero article! 📐 NO 'the math'! Subjects = no article! English grammar rule!"
        },
        {
          type: "mc",
          q: "She doesn't like _____ . She thinks it's boring.",
          options: ["history", "the history", "a history"],
          a: "history",
          tip: "Zero article with school subjects! History = no 'the'!",
          successtip: "✓ HISTORY! No article! 📚 School subjects = bare! Common error corrected! Learn the rule!"
        },
        {
          type: "mc",
          q: "My son loves _____ . He wants to be a scientist!",
          options: ["science", "the science", "a science"],
          a: "science",
          tip: "Science = zero article! Subject names = no article!",
          successtip: "✓ SCIENCE! Zero article! 🔬 Future scientist! Subjects = no 'the'! Grammar mastered!"
        },
        {
          type: "mc",
          q: "ERROR TÍPICO: Which is WRONG? 'I study _____.'",
          options: ["the English", "English", "math"],
          a: "the English",
          tip: "NEVER 'the English' as subject! Just 'English'!",
          successtip: "✓ THE ENGLISH = WRONG! 🚫 Common Spanish error! Say 'English'! No article with subjects!"
        },
        {
          type: "mc",
          q: "They think _____ is difficult but useful.",
          options: ["English", "the English", "an English"],
          a: "English",
          tip: "'English' = subject name! No article needed!",
          successtip: "✓ ENGLISH! Zero article! 🗣️ Difficult BUT useful! Honest opinion! Keep studying!"
        },
        {
          type: "mc",
          q: "I don't like _____ , but I need it for work.",
          options: ["geography", "the geography", "a geography"],
          a: "geography",
          tip: "Geography = subject! No article! Need ≠ like!",
          successtip: "✓ GEOGRAPHY! Zero article! 🌍 Need ≠ like! Practical learning! Work requires it!"
        },
        {
          type: "mc",
          q: "My daughter loves _____ . She's very creative!",
          options: ["art", "the art", "an art"],
          a: "art",
          tip: "'Art' as school subject = zero article!",
          successtip: "✓ ART! No article! 🎨 Creative kid! Art = subject! Talent + passion! Support it!"
        },
        {
          type: "mc",
          q: "He thinks _____ is boring. He prefers numbers.",
          options: ["literature", "the literature", "a literature"],
          a: "literature",
          tip: "Literature = subject! Zero article! Preferences vary!",
          successtip: "✓ LITERATURE! Zero article! 📖 Prefer numbers = math person! Different talents! Valid!"
        },
        {
          type: "mc",
          q: "We study _____ and _____ at school.",
          options: ["math, science", "the math, the science", "a math, a science"],
          a: "math, science",
          tip: "Multiple subjects = zero article for both!",
          successtip: "✓ MATH, SCIENCE! Zero article! 📚 List subjects = no 'the'! Grammar rule consistent!"
        },
        {
          type: "mc",
          q: "I want to learn _____ because it's important for my job.",
          options: ["English", "the English", "an English"],
          a: "English",
          tip: "'English' as language/subject = zero article!",
          successtip: "✓ ENGLISH! Zero article! 💼 Career motivation! Language = tool! Job advancement! Smart goal!"
        }
      ]
    },

    // ===================================================================
    // SET 3: Work Vocabulary (job-related opinions) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set3",
      title: "Work Opinions",
      questions: [
        {
          type: "mc",
          q: "I like my job because my boss _____ very nice.",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Boss' = singular! Is! Good boss = better job!",
          successtip: "✓ IS! Boss = singular! 😊 Nice boss = happy work! Workplace culture matters! Lucky!"
        },
        {
          type: "mc",
          q: "She doesn't like her schedule. It's too _____ .",
          options: ["early", "earlier", "earliest"],
          a: "early",
          tip: "'Too early' = complaint! Base adjective after 'too'!",
          successtip: "✓ TOO EARLY! Schedule complaint! ⏰ Early shifts = hard! Sleep matters! Valid concern!"
        },
        {
          type: "mc",
          q: "They love their coworkers because they _____ friendly.",
          options: ["are", "is", "be"],
          a: "are",
          tip: "'Coworkers' = plural! Are! Good team = happiness!",
          successtip: "✓ ARE! Plural coworkers! 👥 Friendly team = better life! Work friends = support! Blessed!"
        },
        {
          type: "mc",
          q: "I hate _____ . They're too long and boring!",
          options: ["meetings", "the meetings", "meeting"],
          a: "meetings",
          tip: "Plural noun! No article for general statement!",
          successtip: "✓ MEETINGS! Universal hate! 😴 Long meetings = time waste! Everyone agrees! Office life!"
        },
        {
          type: "mc",
          q: "He doesn't like _____ overtime. He wants family time.",
          options: ["working", "to work", "work"],
          a: "working",
          tip: "Like/dislike + -ing! 'Working overtime' = gerund!",
          successtip: "✓ WORKING! Gerund form! 👨‍👩‍👧‍👦 Family > overtime! Work-life balance! Priorities right!"
        },
        {
          type: "mc",
          q: "We love our job because the salary _____ good.",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Salary' = singular! Is! Money matters!",
          successtip: "✓ IS! Salary = singular! 💰 Good pay = love job! Financial stability! Worth it!"
        },
        {
          type: "mc",
          q: "She likes _____ with customers. She's very social!",
          options: ["working", "to work", "work"],
          a: "working",
          tip: "Like + -ing! 'Working with' = gerund + preposition!",
          successtip: "✓ WORKING! Like + -ing! 🤝 Customer service = people skills! Social job = perfect fit!"
        },
        {
          type: "mc",
          q: "I don't like my _____ schedule. It changes every week!",
          options: ["work", "working", "works"],
          a: "work",
          tip: "'Work schedule' = compound noun! Work = adjective!",
          successtip: "✓ WORK schedule! Compound noun! 📅 Changing schedule = planning hard! Stability wanted!"
        },
        {
          type: "mc",
          q: "They hate _____ at night. It's dangerous.",
          options: ["working", "to work", "work"],
          a: "working",
          tip: "Hate + -ing! Night shift = gerund!",
          successtip: "✓ WORKING! Hate + -ing! 🌙 Night shifts = safety concern! Valid fear! Day shift better!"
        },
        {
          type: "mc",
          q: "He loves his job because he _____ learn new things every day.",
          options: ["can", "cans", "could"],
          a: "can",
          tip: "'Can' + base verb! Learning = growth! Positive!",
          successtip: "✓ CAN! Love + reason! 📈 Learning = growth! Career development! Engaged employee! Success!"
        }
      ]
    },

    // ===================================================================
    // SET 4: Hobbies & Pastimes (retomar Units anteriores) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set4",
      title: "Hobbies & Pastimes",
      questions: [
        {
          type: "mc",
          q: "I like _____ soccer on weekends. It's great exercise!",
          options: ["playing", "to play", "play"],
          a: "playing",
          tip: "Like + -ing! Playing = gerund! More natural for hobbies!",
          successtip: "✓ PLAYING! Like + -ing! ⚽ Hobbies = gerund! Natural English! Weekend activity!"
        },
        {
          type: "mc",
          q: "She loves _____ movies at home. Netflix is her friend!",
          options: ["watching", "to watch", "watch"],
          a: "watching",
          tip: "Love + -ing! Watching = gerund! Home entertainment!",
          successtip: "✓ WATCHING! Love + -ing! 📺 Netflix life! Relaxation time! Couch = happy place!"
        },
        {
          type: "mc",
          q: "They don't like _____ to the gym. They prefer the park.",
          options: ["going", "to go", "go"],
          a: "going",
          tip: "Don't like + -ing! Going = gerund! Preference!",
          successtip: "✓ GOING! Like + -ing! 🏋️ Gym ≠ for everyone! Park = free + fresh air! Valid choice!"
        },
        {
          type: "mc",
          q: "ERROR TÍPICO: Which is MORE NATURAL? 'I like _____.'",
          options: ["swimming", "to swim", "swim"],
          a: "swimming",
          tip: "For hobbies, -ING is MORE NATURAL! Not wrong, but less common!",
          successtip: "✓ SWIMMING! Natural choice! 🏊 Like + -ing = hobbies! 'Like to swim' = less common! Use -ing!"
        },
        {
          type: "mc",
          q: "We love _____ books before bed. It helps us sleep.",
          options: ["reading", "to read", "read"],
          a: "reading",
          tip: "Love + -ing! Reading = gerund! Bedtime routine!",
          successtip: "✓ READING! Love + -ing! 📚 Bedtime ritual! Books = better sleep! Healthy habit!"
        },
        {
          type: "mc",
          q: "He doesn't like _____ . He prefers staying home.",
          options: ["dancing", "to dance", "dance"],
          a: "dancing",
          tip: "Don't like + -ing! Preference stated! Introvert valid!",
          successtip: "✓ DANCING! Not everyone likes it! 💃 Introvert = valid! Stay home = comfort! Different personalities!"
        },
        {
          type: "mc",
          q: "I hate _____ early on weekends. I want to rest!",
          options: ["waking up", "to wake up", "wake up"],
          a: "waking up",
          tip: "Hate + -ing! Two-word verb = gerund together!",
          successtip: "✓ WAKING UP! Hate + -ing! 😴 Weekends = sleep in! Rest = deserved! No alarm clock!"
        },
        {
          type: "mc",
          q: "She loves _____ because it's relaxing and creative.",
          options: ["cooking", "to cook", "cook"],
          a: "cooking",
          tip: "Love + -ing! Cooking = gerund! Hobby + reason!",
          successtip: "✓ COOKING! Love + -ing! 👩‍🍳 Cooking = therapy! Creative outlet! Relaxation + food!"
        },
        {
          type: "mc",
          q: "They like _____ music while they work. It helps focus.",
          options: ["listening to", "to listen to", "listen to"],
          a: "listening to",
          tip: "Like + -ing! 'Listening to' = gerund + preposition!",
          successtip: "✓ LISTENING TO! Gerund + prep! 🎵 Music = productivity! Focus tool! Work strategy!"
        },
        {
          type: "mc",
          q: "I don't like _____ video games. I prefer real activities!",
          options: ["playing", "to play", "play"],
          a: "playing",
          tip: "Don't like + -ing! Preference for active lifestyle!",
          successtip: "✓ PLAYING! Don't like -ing! 🎮 Active > screens! Personal preference! Outdoor person!"
        }
      ]
    },

    // ===================================================================
    // SET 5: Opinions + Adjectives (think + adjective) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set5",
      title: "Opinions with Adjectives",
      questions: [
        {
          type: "mc",
          q: "I think soccer _____ exciting! The games are great!",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Think' + subject + 'is/are' + adjective! Opinion!",
          successtip: "✓ IS! Think + opinion! ⚽ Soccer = singular! I think X is Y! Opinion structure!"
        },
        {
          type: "mc",
          q: "ERROR TÍPICO: Which is MORE NATURAL? 'I think _____.'",
          options: ["the movie is boring", "that the movie is boring", "the movie boring"],
          a: "the movie is boring",
          tip: "DROP 'that'! Native = shorter! 'I think X is Y' direct!",
          successtip: "✓ DROP 'THAT'! Shorter = better! 🎬 I think X is Y! No extra words! Native style!"
        },
        {
          type: "mc",
          q: "She thinks math _____ difficult. She studies hard!",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Math' = singular subject! Is! Opinion + reality!",
          successtip: "✓ IS! Math = tough! 📐 Difficult BUT studies! Honest + effort! Keep trying!"
        },
        {
          type: "mc",
          q: "They think the restaurant _____ expensive. They go rarely.",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Restaurant' = singular! Opinion affects behavior!",
          successtip: "✓ IS! Expensive = rarely go! 💰 Budget matters! Price affects frequency! Smart spending!"
        },
        {
          type: "mc",
          q: "I think learning English _____ important for my future.",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Learning English' = gerund as subject = singular!",
          successtip: "✓ IS! Learning = singular! 📚 English = future! Career investment! Smart thinking!"
        },
        {
          type: "mc",
          q: "We think our boss _____ very fair. We're lucky!",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Boss' = singular! Positive opinion! Workplace!",
          successtip: "✓ IS! Fair boss! 👔 Good leadership = happy workers! Lucky team! Appreciate it!"
        },
        {
          type: "mc",
          q: "He thinks his coworkers _____ lazy. He works harder.",
          options: ["are", "is", "be"],
          a: "are",
          tip: "'Coworkers' = plural! Are! Negative opinion!",
          successtip: "✓ ARE! Coworkers = plural! 😤 Frustration valid! Carry the weight! Team imbalance!"
        },
        {
          type: "mc",
          q: "I think working two jobs _____ very tiring.",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Working two jobs' = gerund phrase = singular!",
          successtip: "✓ IS! Gerund = singular! 😴 Two jobs = exhausting! Immigrant hustle! Sacrifice real!"
        },
        {
          type: "mc",
          q: "She thinks the park _____ beautiful. She goes every day!",
          options: ["is", "are", "be"],
          a: "is",
          tip: "'Park' = singular! Opinion = action! Beauty = motivation!",
          successtip: "✓ IS! Park = beautiful! 🌳 Opinion → action! Daily walks! Nature = happiness!"
        },
        {
          type: "mc",
          q: "They think movies _____ too expensive now. Ticket prices high!",
          options: ["are", "is", "be"],
          a: "are",
          tip: "'Movies' = plural! Are! Economic opinion!",
          successtip: "✓ ARE! Movies = plural! 🎬 Inflation real! $15+ tickets! Stay home cheaper! Netflix wins!"
        }
      ]
    },

    // ===================================================================
    // SET 6: Invitations (Would you like...? / Do you want...?) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set6",
      title: "Making Invitations",
      questions: [
        {
          type: "mc",
          q: "_____ you like to go to the movies this weekend?",
          options: ["Would", "Do", "Are"],
          a: "Would",
          tip: "'Would you like' = polite invitation! Formal offer!",
          successtip: "✓ WOULD! Polite invitation! 🎬 Would = formal! Friends = polite still! Making plans!"
        },
        {
          type: "mc",
          q: "Do you _____ to play soccer with us Saturday?",
          options: ["want", "wants", "wanted"],
          a: "want",
          tip: "'Do you want' = casual invitation! Friends level!",
          successtip: "✓ WANT! Casual invite! ⚽ Do you want = friends! Group activity! Join the team!"
        },
        {
          type: "mc",
          q: "Would you like _____ come to dinner at my house?",
          options: ["to", "for", "at"],
          a: "to",
          tip: "'Would you like TO' + base verb! Infinitive structure!",
          successtip: "✓ TO! Would like TO! 🍽️ Dinner invitation! Home hospitality! Building friendship!"
        },
        {
          type: "mc",
          q: "_____ to grab coffee after work? Quick chat!",
          options: ["Want", "Wanna", "You want"],
          a: "Want",
          tip: "Very casual = can drop 'Do you'! 'Want to grab' = informal!",
          successtip: "✓ WANT! Super casual! ☕ Drop 'Do you'! Close friends! Quick coffee = connection!"
        },
        {
          type: "mc",
          q: "Would you like to _____ a game at the stadium?",
          options: ["watch", "watching", "watched"],
          a: "watch",
          tip: "'Would you like to' + BASE FORM verb!",
          successtip: "✓ WATCH! Base form! 🏟️ Stadium invitation! Sports event! Group outing! Fun time!"
        },
        {
          type: "mc",
          q: "Do you want _____ see the new movie at the cinema?",
          options: ["to", "for", "at"],
          a: "to",
          tip: "'Do you want TO' + base verb! Structure consistent!",
          successtip: "✓ TO! Want TO! 🎥 New movie! Cinema outing! Entertainment plans! Social time!"
        },
        {
          type: "mc",
          q: "_____ hang out this weekend? We can go to the park!",
          options: ["Want to", "You want", "Do you want"],
          a: "Want to",
          tip: "Super casual with friends = drop 'Do you'! Very informal!",
          successtip: "✓ WANT TO! Ultra casual! 🌳 Hang out = spend time! Park = free! Friend language!"
        },
        {
          type: "mc",
          q: "Would you like _____ go to a restaurant Friday night?",
          options: ["to", "for", "at"],
          a: "to",
          tip: "'Would you like to' + verb! Polite dinner invitation!",
          successtip: "✓ TO! Would like TO! 🍽️ Friday dinner! Date night! Friend outing! Social plans!"
        },
        {
          type: "mc",
          q: "Do you _____ to come to my birthday party?",
          options: ["want", "wants", "wanted"],
          a: "want",
          tip: "'Do you want' = casual party invitation! Friendly!",
          successtip: "✓ WANT! Party invite! 🎉 Birthday celebration! Friend gathering! Say yes!"
        },
        {
          type: "mc",
          q: "_____ like to meet at 7pm? Does that work for you?",
          options: ["Would you", "Do you", "You would"],
          a: "Would you",
          tip: "'Would you like to meet' = polite time suggestion!",
          successtip: "✓ WOULD YOU! Polite timing! ⏰ Flexible planning! Asking preference! Considerate!"
        }
      ]
    },

    // ===================================================================
    // SET 7: Social Places (where to go) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set7",
      title: "Social Places",
      questions: [
        {
          type: "mc",
          q: "Let's go _____ the movies! There's a good film!",
          options: ["to", "at", "in"],
          a: "to",
          tip: "'Go TO' + place! Movement direction!",
          successtip: "✓ TO! Go TO places! 🎬 Movies = cinema! Social activity! Entertainment outing!"
        },
        {
          type: "mc",
          q: "Would you like to go to _____ restaurant downtown?",
          options: ["a", "the", "that"],
          a: "a",
          tip: "'A restaurant' = any restaurant! General invitation!",
          successtip: "✓ A! Indefinite article! 🍽️ A = any one! Flexible! Downtown = city center!"
        },
        {
          type: "mc",
          q: "Do you want to watch the game _____ the stadium?",
          options: ["at", "in", "to"],
          a: "at",
          tip: "'At the stadium' = location! Watch AT!",
          successtip: "✓ AT! Watch AT stadium! 🏟️ Location = at! Live game! Exciting atmosphere!"
        },
        {
          type: "mc",
          q: "Let's meet _____ the park! It's nice weather!",
          options: ["at", "to", "in"],
          a: "at",
          tip: "'Meet AT' = meeting point! Location!",
          successtip: "✓ AT! Meet AT park! 🌳 Nice weather = outdoor! Fresh air! Free activity!"
        },
        {
          type: "mc",
          q: "Would you like to go to _____ mall? Shopping time!",
          options: ["the", "a", "that"],
          a: "the",
          tip: "'The mall' = specific one in your area! Definite!",
          successtip: "✓ THE! Definite mall! 🛍️ The = known place! Local mall! Shopping + walking!"
        },
        {
          type: "mc",
          q: "Let's go _____ a café! I need coffee!",
          options: ["to", "at", "in"],
          a: "to",
          tip: "'Go TO' + place! Movement to location!",
          successtip: "✓ TO! Go TO café! ☕ Need coffee! Social + caffeine! Meeting spot!"
        },
        {
          type: "mc",
          q: "Do you want to go _____ the beach this Sunday?",
          options: ["to", "at", "in"],
          a: "to",
          tip: "'Go TO the beach' = movement! Direction!",
          successtip: "✓ TO! Go TO beach! 🏖️ Sunday plans! Relaxation! Nature + family time!"
        },
        {
          type: "mc",
          q: "Would you like to visit _____ museum? It's free today!",
          options: ["the", "a", "that"],
          a: "the",
          tip: "'The museum' = specific known museum!",
          successtip: "✓ THE! Specific museum! 🏛️ Free = great deal! Culture + learning! Take advantage!"
        },
        {
          type: "mc",
          q: "Let's go _____ downtown! Lots of things to do!",
          options: ["to", "at", "in"],
          a: "to",
          tip: "'Go TO downtown' = direction! City center!",
          successtip: "✓ TO! Go TO downtown! 🏙️ City center! Many options! Explore + fun!"
        },
        {
          type: "mc",
          q: "Do you want to have dinner _____ my favorite restaurant?",
          options: ["at", "to", "in"],
          a: "at",
          tip: "'Have dinner AT' = location! At + specific place!",
          successtip: "✓ AT! Dinner AT restaurant! 🍽️ Favorite place! Personal recommendation! Good food!"
        }
      ]
    },

    // ===================================================================
    // SET 8: Accepting/Declining Invitations (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set8",
      title: "Accept or Decline",
      questions: [
        {
          type: "mc",
          q: "ACCEPT enthusiastically: '_____ That sounds great!'",
          options: ["Yes, I'd love to!", "No, I can't.", "Maybe later."],
          a: "Yes, I'd love to!",
          tip: "'I'd love to' = enthusiastic yes! Very positive!",
          successtip: "✓ I'D LOVE TO! Enthusiastic yes! 🎉 Show excitement! Positive response! Building friendship!"
        },
        {
          type: "mc",
          q: "DECLINE politely: 'I'm sorry, I can't. I _____ work.'",
          options: ["have to", "has to", "want to"],
          a: "have to",
          tip: "'Have to' = obligation! Excuse with reason! Polite!",
          successtip: "✓ HAVE TO! Polite decline! 😔 Reason = respectful! Obligation explained! Rain check possible!"
        },
        {
          type: "mc",
          q: "ACCEPT casually: 'Sure! _____ time?'",
          options: ["What", "Why", "Where"],
          a: "What",
          tip: "'What time?' = confirming details! Practical question!",
          successtip: "✓ WHAT time? Casual accept! ⏰ Confirming plans! Practical! Moving forward!"
        },
        {
          type: "mc",
          q: "DECLINE with regret: 'I'd love to, _____ I'm busy that day.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = contrast! Want to go BUT can't! Regret!",
          successtip: "✓ BUT! Regret + excuse! 😔 Want to BUT busy! Contrast! Maybe next time!"
        },
        {
          type: "mc",
          q: "ACCEPT with excitement: '_____ ! I'm down!'",
          options: ["Yes", "No", "Maybe"],
          a: "Yes",
          tip: "'I'm down' = slang for 'I agree'! Very casual!",
          successtip: "✓ I'M DOWN! Slang! 🔥 Down = agree! Young/casual! Enthusiastic yes! Street language!"
        },
        {
          type: "mc",
          q: "SOFT DECLINE: '_____ next time! I'm tired today.'",
          options: ["Maybe", "Yes", "Sure"],
          a: "Maybe",
          tip: "'Maybe next time' = soft no! Leaves door open!",
          successtip: "✓ MAYBE! Soft decline! 😴 Not closing door! Tired = valid! Future possibility!"
        },
        {
          type: "mc",
          q: "ACCEPT with question: 'That sounds fun! _____ are we going?'",
          options: ["Where", "Why", "Who"],
          a: "Where",
          tip: "'Where' = asking location! Accepting + asking details!",
          successtip: "✓ WHERE! Accept + ask! 📍 Sounds fun + details! Practical planning! Engaged!"
        },
        {
          type: "mc",
          q: "DECLINE: 'I wish I _____ , but I have to take care of my kids.'",
          options: ["could", "can", "would"],
          a: "could",
          tip: "'I wish I could' = expressing inability! Regret!",
          successtip: "✓ COULD! Wish + could! 👶 Family priority! Kids = responsibility! Honest excuse!"
        },
        {
          type: "mc",
          q: "ENTHUSIASTIC ACCEPT: 'Absolutely! _____ can't wait!'",
          options: ["I", "You", "We"],
          a: "I",
          tip: "'I can't wait' = super excited! Very enthusiastic!",
          successtip: "✓ I CAN'T WAIT! Maximum excitement! 🎉 Absolutely = strong yes! Eager! Great energy!"
        },
        {
          type: "mc",
          q: "RAIN CHECK (postpone): 'Can we do it _____ time? I'm swamped.'",
          options: ["another", "other", "the other"],
          a: "another",
          tip: "'Another time' = different occasion! Postpone request!",
          successtip: "✓ ANOTHER! Rain check! 📅 Swamped = too busy! Reschedule! Still interested!"
        }
      ]
    },

    // ===================================================================
    // SET 9: Making Plans (time + place) (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set9",
      title: "Making Plans",
      questions: [
        {
          type: "mc",
          q: "Let's meet _____ Saturday at 7pm!",
          options: ["on", "in", "at"],
          a: "on",
          tip: "'On' + day! 'At' + time! Two prepositions!",
          successtip: "✓ ON! Day preposition! 📅 On Saturday, at 7pm! Preposition rules! Clear plans!"
        },
        {
          type: "mc",
          q: "_____ about the new restaurant downtown?",
          options: ["How", "What", "Why"],
          a: "How",
          tip: "'How about' = making suggestion! Common phrase!",
          successtip: "✓ HOW ABOUT! Suggestion phrase! 🍽️ Proposing idea! Casual planning! Good choice!"
        },
        {
          type: "mc",
          q: "What time _____ for you? Morning or afternoon?",
          options: ["works", "work", "working"],
          a: "works",
          tip: "'What time works' = asking preference! Third person!",
          successtip: "✓ WORKS! Time = singular! ⏰ Asking preference! Flexible! Considerate planning!"
        },
        {
          type: "mc",
          q: "Let's go _____ 6pm. Is that okay?",
          options: ["at", "on", "in"],
          a: "at",
          tip: "'At' + specific time! Clock time = at!",
          successtip: "✓ AT! Time preposition! ⏰ At + clock time! Grammar rule! Suggesting time!"
        },
        {
          type: "mc",
          q: "_____ meet at the mall entrance? Easy to find!",
          options: ["Let's", "We", "Do"],
          a: "Let's",
          tip: "'Let's' = suggestion for 'us'! Inclusive invitation!",
          successtip: "✓ LET'S! Suggestion! 🛍️ Let's = let us! Meeting point! Clear location!"
        },
        {
          type: "mc",
          q: "Do you want to go _____ the morning or the evening?",
          options: ["in", "at", "on"],
          a: "in",
          tip: "'In' + parts of day! In the morning/evening!",
          successtip: "✓ IN! Time period! 🌅 In + morning/evening! Flexible timing! Asking preference!"
        },
        {
          type: "mc",
          q: "_____ we meet earlier? 6pm instead of 7pm?",
          options: ["Can", "Do", "Are"],
          a: "Can",
          tip: "'Can we' = asking if possible! Change request!",
          successtip: "✓ CAN! Possibility question! ⏰ Earlier = flexible! Asking permission! Polite adjustment!"
        },
        {
          type: "mc",
          q: "I can meet you _____ Friday night. Does that work?",
          options: ["on", "in", "at"],
          a: "on",
          tip: "'On' + day! Friday night = specific day + time!",
          successtip: "✓ ON! Day preposition! 🌙 On Friday night! Clear timing! Confirming availability!"
        },
        {
          type: "mc",
          q: "Let's go to the movies and _____ grab dinner after!",
          options: ["then", "than", "when"],
          a: "then",
          tip: "'Then' = sequence! Movies first, dinner after!",
          successtip: "✓ THEN! Sequence word! 🎬🍽️ Two activities! Full evening! Good planning!"
        },
        {
          type: "mc",
          q: "What _____ best for you? Saturday or Sunday?",
          options: ["works", "work", "working"],
          a: "works",
          tip: "'What works' = asking preference! Subject-verb agreement!",
          successtip: "✓ WORKS! What = singular! 📅 Weekend options! Flexible! Considerate friend!"
        }
      ]
    },

    // ===================================================================
    // SET 10: Integration with BECAUSE/SO/BUT (10 preguntas)
    // ===================================================================
    {
      id: "m3-u16-set10",
      title: "Opinions + Connectors",
      questions: [
        {
          type: "mc",
          q: "I like soccer _____ it's exciting and fun to watch!",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason! Why you like it! Cause!",
          successtip: "✓ BECAUSE! Reason! ⚽ Like + BECAUSE = explain why! Logical connection! B1 skill!"
        },
        {
          type: "mc",
          q: "She loves cooking, _____ she goes to restaurants often.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = result! Love cooking → go to restaurants! Effect!",
          successtip: "✓ SO! Result! 👩‍🍳 Love cooking SO explore! Cause → effect! Food passion!"
        },
        {
          type: "mc",
          q: "I want to go to the movies, _____ I don't have money.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = contrast! Want to go BUT can't! Conflict!",
          successtip: "✓ BUT! Conflict! 🎬💰 Want BUT broke! Reality check! Desire ≠ possibility!"
        },
        {
          type: "mc",
          q: "They don't like the park _____ it's too crowded on weekends.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason for dislike! Explains why not!",
          successtip: "✓ BECAUSE! Reason for dislike! 🌳👥 Too crowded = reason! Valid complaint! Prefer quiet!"
        },
        {
          type: "mc",
          q: "I really like my job, _____ the salary is good and the people are nice.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' + TWO reasons! Job satisfaction explained!",
          successtip: "✓ BECAUSE! Double reason! 💼 Money + people = happy! Complete satisfaction! Lucky!"
        },
        {
          type: "mc",
          q: "He loves playing soccer, _____ he plays every weekend.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = action result! Love → play regularly!",
          successtip: "✓ SO! Action result! ⚽ Love → consistent play! Passion = commitment! Hobby life!"
        },
        {
          type: "mc",
          q: "I want to learn English, _____ I don't have time to study.",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = desire vs reality! Want BUT time issue!",
          successtip: "✓ BUT! Desire vs obstacle! 📚⏰ Want BUT busy! Common struggle! Need strategy!"
        },
        {
          type: "mc",
          q: "We like going to restaurants _____ we don't like cooking at home.",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason! Don't cook → eat out! Logic!",
          successtip: "✓ BECAUSE! Reason! 🍽️ Don't cook = eat out! Logical connection! Restaurant life!"
        },
        {
          type: "mc",
          q: "She hates her schedule, _____ she's looking for a new job.",
          options: ["so", "because", "but"],
          a: "so",
          tip: "'So' = consequence! Hate schedule → job search!",
          successtip: "✓ SO! Action taken! 💼 Unhappy → change! Cause → solution! Taking control!"
        },
        {
          type: "mc",
          q: "I love the beach, _____ I can't swim very well!",
          options: ["but", "so", "because"],
          a: "but",
          tip: "'But' = irony/contrast! Love beach BUT can't swim!",
          successtip: "✓ BUT! Funny contrast! 🏖️🏊 Love beach BUT can't swim! Ironic! Learn to swim!"
}
      ]
    }
  ],  
// ===================================================================
// READING SET: The Invitation (Social Interaction & Negotiation)
// ===================================================================
readingSet: {
  id: "m3-u16-reading",
  title: "Reading: Planning a Night Out",
  category: "reading",

  questions: [
    {
      type: "passage",
      passage: `**The Message**

"Hi Maria! Would you like to go to the movies this Saturday? There's a new action movie at the cinema downtown. We can meet at 7pm at the entrance. After the movie, we can go to that Italian restaurant you like. Let me know if you can come! - Carlos"`
    },
    {
      type: "mc",
      q: "What exactly is Carlos inviting Maria to do?",
      options: ["Go to the movies and dinner", "Go to an Italian restaurant only", "Meet at 7pm at his house"],
      a: "Go to the movies and dinner",
      tip: "Busca las dos actividades principales en el mensaje de Carlos.",
      successtip: "✓ DOUBLE PLAN!\nCarlos suggests a movie followed by food at an Italian place. 🎬🍽️"
    },
    {
      type: "mc",
      q: "What time does Carlos suggest for the meeting?",
      options: ["7pm", "Saturday", "Downtown"],
      a: "7pm",
      successtip: "✓ SPECIFIC TIME!\n'At 7pm' is the clearly stated meeting time. ⏰"
    },
    {
      type: "mc",
      q: "Where exactly does Carlos want to meet Maria?",
      options: ["At the cinema entrance", "At the restaurant", "At Maria's house"],
      a: "At the cinema entrance",
      successtip: "✓ MEETING POINT!\nHe specifies 'at the entrance' of the cinema. 📍"
    },
    {
      type: "mc",
      q: "Is Carlos's invitation style formal, casual, or polite?",
      options: ["Polite and friendly (Would you like)", "Very formal (Sir/Madam)", "Very casual (Wanna)"],
      a: "Polite and friendly (Would you like)",
      tip: "'Would you like' es la forma estándar de invitar educadamente.",
      successtip: "✓ POLITE TONE!\nHe uses 'Would you like,' which is professional yet friendly. 🤝"
    },
    {
      type: "mc",
      q: "What does Carlos imply about the Italian restaurant?",
      options: ["Maria already knows and likes it", "He has never been there", "It's a brand new place"],
      a: "She likes it (you like)",
      successtip: "✓ THOUGHTFUL FRIEND!\nHe says 'that restaurant YOU LIKE,' showing he remembers her preferences. 🍝"
    },

    

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Maria's Response (preguntas 4-6 y 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Reply**

"Hi Carlos! I'd love to go! Movies sound great! But I can't do Saturday because I have to work. Can we do Sunday instead? Same time works for me. I really want to try that restaurant!"`
    },
    {
      type: "mc",
      q: "Based on her response, does Maria accept the invitation?",
      options: ["She accepts but changes the day", "She completely declines", "She only wants dinner"],
      a: "She accepts but changes the day",
      tip: "Nota que ella dice que 'le encantaría', pero propone una alternativa.",
      successtip: "✓ FLEXIBLE ACCEPTANCE!\nShe is interested but needs to move the plan to Sunday. 🔄"
    },
    {
      type: "mc",
      q: "Why is Maria unable to go on Saturday?",
      options: ["She has to work", "She doesn't like movies", "She is too tired"],
      a: "She has to work",
      successtip: "✓ VALID REASON!\nWork is her obligation ('I have to work'). 💼"
    },
    {
      type: "mc",
      q: "Which connector shows that Maria wants to go but has a conflict?",
      options: ["But", "Because", "So"],
      a: "But (wants to go but can't on Saturday)",
      tip: "'But' se usa para contrastar un deseo con un obstáculo.",
      successtip: "✓ CONTRAST!\n'But' perfectly shows the shift from her desire to her limitation. 🔀"
    },
    {
      type: "mc",
      q: "Which phrases show Maria is enthusiastic about the plan?",
      options: ["I'd love to! / sounds great!", "Maybe / I don't know", "I can't"],
      a: "I'd love to! / sounds great! / I really want!",
      successtip: "✓ ENTHUSIASM!\nShe uses multiple positive expressions to show she is excited. 🎉"
    },
  {
      type: "passage",
      passage: `**The Final Plan**

The final arrangement includes two main parts: the entertainment and the meal. Maria and Carlos are negotiating to make sure the time and day work for both.`
    },
    {
      type: "mc",
      q: "How many total activities are included in this social plan?",
      options: ["Two (movie + dinner)", "One (only movie)", "Three (movie, dinner, and dancing)"],
      a: "Two (movie + dinner)",
      successtip: "✓ READING COMPLETE!\nExcellent! You identified all the components of this social interaction. 🏆"
    }
  ]
},

    ReviewSet: {
      id: "m3-u16-review",
      title: "Module 3 Final Review",
      questions: [
        {
          type: "match",
          q: "Final Review: Match language contexts",
          pairs: [
            { left: "Like/Love/Hate", right: "Express opinions" },
            { left: "Would you like to...?", right: "Polite invitation" },
            { left: "Want to grab...?", right: "Casual invitation" }
          ],
          a: "completed",
          tip: "Match each structure to its use!",
          successtip: "✓ CONTEXTS MASTERED! 🎯 Formal vs casual! Like = opinions! Invitations = social! Context is key!"
        },
        {
          type: "mc",
          q: "ERROR REVIEW: Which is WRONG? '_____'",
          options: ["I like me soccer", "I like soccer", "I love soccer"],
          a: "I like me soccer",
          tip: "Spanish error! No 'me' in English!",
          successtip: "✓ I LIKE ME = WRONG! 🚫 Spanish translation! English = I like X! Error corrected!"
        },
        {
          type: "mc",
          q: "ZERO ARTICLE: Which is correct? 'I study _____.'",
          options: ["math", "the math", "a math"],
          a: "math",
          tip: "Subjects = zero article! No 'the'!",
          successtip: "✓ MATH! Zero article! 📐 Subjects = no article! Grammar rule mastered!"
        },
        {
          type: "mc",
          q: "GERUND: Which is MORE NATURAL? 'I like _____ on weekends.'",
          options: ["swimming", "to swim", "swim"],
          a: "swimming",
          tip: "Hobbies = -ING! Like + gerund = natural!",
          successtip: "✓ SWIMMING! Like + -ing! 🏊 Hobbies = gerund! Natural English choice!"
        },
        {
          type: "mc",
          q: "INVITATION: Polite = '_____ you like to go?' Casual = '_____ you want to go?'",
          options: ["Would / Do", "Do / Would", "Are / Do"],
          a: "Would / Do",
          tip: "Would = formal! Do = casual! Register difference!",
          successtip: "✓ WOULD vs DO! Politeness levels! 🎯 Context determines choice! Social skills!"
        },
        {
          type: "mc",
          q: "CONNECTOR: 'I like soccer _____ it's exciting.'",
          options: ["because", "so", "but"],
          a: "because",
          tip: "'Because' = reason! Why you like it!",
          successtip: "✓ BECAUSE! Reason connector! ⚽ Like + BECAUSE = explain! B1 logic!"
        },
        {
          type: "mc",
          q: "ACCEPT: Which shows MOST enthusiasm? '_____'",
          options: ["Yes, I'd love to!", "Okay, sure.", "Maybe."],
          a: "Yes, I'd love to!",
          tip: "'I'd love to' = maximum enthusiasm! Very positive!",
          successtip: "✓ I'D LOVE TO! Maximum enthusiasm! 🎉 Excited accept! Building friendships!"
        },
        {
          type: "mc",
          q: "PREPOSITIONS: 'Let's meet _____ Saturday _____ 7pm.'",
          options: ["on / at", "at / on", "in / at"],
          a: "on / at",
          tip: "ON + day! AT + time! Two different prepositions!",
          successtip: "✓ ON / AT! Preposition rules! 📅⏰ Days = on! Time = at! Grammar mastered!"
        },
        {
          type: "input",
          q: "Complete INTEGRATION: 'I _____ (be) good at soccer, and I _____ (like) playing it!'",
          a: "am / love",
          accept: { ignoreCase: true, variants: ["am, love", "am/love", "am, like", "am/like"] },
          tip: "Be + good at! Love/like + -ing! Two structures!",
          successtip: "✓ AM / LOVE! Integration! ⚽ Unit 11 + Unit 16! Abilities + opinions! Combined!"
        },
        {
          type: "mc",
          q: "FINAL B1 SENTENCE: 'I'm good at cooking and I really like it, so I want to be a chef. Would you like to go to a restaurant this weekend?' — How many Units integrated?",
          options: ["All 6 Units (11-16)!", "3 Units", "1 Unit"],
          a: "All 6 Units (11-16)!",
          tip: "Count: Abilities + Can + Adverbs + Modifiers + Want + Like!",
          successtip: "✓ ALL 6 UNITS! B1 MASTERY! 🔥🎉 Unit 11: good at! Unit 12: can! Unit 13: adverbs! Unit 14: really! Unit 15: want! Unit 16: like + invitation! MÓDULO 3 COMPLETE! You can now have REAL conversations in English! From A2 to B1! 🚀🏆 Congratulations! You're ready for the next level!"
        }
      ]
    }
};
